import { DOMAINS, roleRank, type DomainKey, type ModuleMenuItem, type ModuleRoute, type ModuleUi, type ModuleWidget, type OrgInfo } from "@vixio/data";
import type { NavGroup } from "@vixio/ui";
import { registeredModules, type RegisteredModule } from "../modules.generated";

export interface ModuleState {
  /** Módulos aplicados no banco (core.installed_modules). */
  installed: Set<string>;
  /** Flags da organização (ausente = ligado, como core.flag_enabled). */
  flags: Map<string, boolean>;
}

export function isEnabled(m: RegisteredModule, s: ModuleState): boolean {
  if (!s.installed.has(m.name)) return false;
  if (m.name === "core") return true;
  const flag = m.featureFlag ?? m.name;
  return s.flags.get(flag) ?? true;
}

/** Módulos com UI, instalados e ligados na organização. */
export function activeModules(s: ModuleState, all: RegisteredModule[] = registeredModules): ModuleUi[] {
  return all.filter((m) => isEnabled(m, s)).map((m) => m.ui);
}

function allowed(item: { minRole: string; platformAdmin?: boolean }, org: OrgInfo | null, platformAdmin: boolean): boolean {
  if (item.platformAdmin && !platformAdmin) return false;
  return (org?.rank ?? 0) >= roleRank(item.minRole);
}

export function menuItems(mods: ModuleUi[], org: OrgInfo | null, platformAdmin: boolean): Array<ModuleMenuItem & { domain: DomainKey; module: string }> {
  return mods.flatMap((m) =>
    m.menu.filter((it) => allowed(it, org, platformAdmin)).map((it) => ({ ...it, domain: it.domain ?? m.domain, module: m.key })),
  );
}

/** Menu por domínio (ordem do descritivo §8.2), só com o que o papel pode ver. */
export function navGroups(mods: ModuleUi[], org: OrgInfo | null, platformAdmin: boolean): NavGroup[] {
  const items = menuItems(mods, org, platformAdmin);
  return DOMAINS.map((d) => ({
    key: d.key,
    label: d.menu,
    items: items
      .filter((i) => i.domain === d.key)
      .sort((a, b) => (a.order ?? 50) - (b.order ?? 50))
      .map((i) => ({ to: i.path, label: i.label, icon: i.icon, end: i.end })),
  })).filter((g) => g.items.length > 0);
}

export function routesFor(mods: ModuleUi[]): Array<ModuleRoute & { module: string }> {
  return mods.flatMap((m) => m.routes.map((r) => ({ ...r, module: m.key })));
}

export function widgetsFor(mods: ModuleUi[], org: OrgInfo | null): ModuleWidget[] {
  return mods
    .flatMap((m) => m.widgets ?? [])
    .filter((w) => (org?.rank ?? 0) >= roleRank(w.minRole))
    .sort((a, b) => (a.order ?? 50) - (b.order ?? 50));
}

export interface WidgetGroup {
  domain: DomainKey;
  label: string;
  widgets: ModuleWidget[];
}

/**
 * Painéis do Início agrupados pelo domínio do módulo. Os grupos seguem a prioridade dos próprios painéis (o menor
 * `order` do grupo vem primeiro), e dentro do grupo os painéis largos (span 2 ou 3) abrem a seção.
 */
export function widgetGroupsFor(mods: ModuleUi[], org: OrgInfo | null): WidgetGroup[] {
  const rank = org?.rank ?? 0;
  const groups = new Map<DomainKey, ModuleWidget[]>();
  for (const m of mods) {
    for (const w of m.widgets ?? []) {
      if (rank < roleRank(w.minRole)) continue;
      const list = groups.get(m.domain) ?? [];
      list.push(w);
      groups.set(m.domain, list);
    }
  }
  const ord = (w: ModuleWidget) => w.order ?? 50;
  return [...groups.entries()]
    .map(([domain, widgets]) => ({
      domain,
      label: DOMAINS.find((d) => d.key === domain)?.title ?? domain,
      widgets: [...widgets].sort((a, b) => ((b.span ?? 1) > 1 ? 1 : 0) - ((a.span ?? 1) > 1 ? 1 : 0) || ord(a) - ord(b)),
    }))
    .sort((a, b) => Math.min(...a.widgets.map(ord)) - Math.min(...b.widgets.map(ord)));
}

export { registeredModules };
